import * as React from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import { ThemeProvider } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import theme from '../theme';
import logoBlack from '../Login/logo_black.png';
import logoWhite from '../Login/logo_white.png';
import { grey, blue } from '@mui/material/colors';
import { CircularProgress, Paper, Link as MuiLink } from '@mui/material';
import { useLocation } from 'react-router-dom';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';

export default function TwoFactorVerify() {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();

  const [code, setCode] = React.useState('');
  const [trustDevice, setTrustDevice] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isResending, setIsResending] = React.useState(false);
  const [resendCooldown, setResendCooldown] = React.useState(0);

  const [errorMsg, setErrormsg] = React.useState({
    severity: 'error',
    msg: '',
    show: false,
  });

  const [successMsg, setSuccessMsg] = React.useState({
    msg: '',
    show: false,
  });

  // Redireciona se nao houver pending 2FA
  React.useEffect(() => {
    if (!auth.pending2FA) {
      navigate('/login');
    }
  }, [auth.pending2FA, navigate]);

  // Cooldown timer para reenvio
  React.useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleSubmit = (event) => {
    event.preventDefault();
    setErrormsg((prev) => ({ ...prev, show: false }));
    setSuccessMsg((prev) => ({ ...prev, show: false }));

    if (!code || code.length !== 6) {
      setErrormsg({
        show: true,
        msg: 'Digite o codigo de 6 digitos.',
        severity: 'warning',
      });
      return;
    }

    setIsLoading(true);

    let from = location.state?.from?.pathname || '/';
    const redirectTo = () => {
      navigate(from, { replace: true });
    };
    const cbErr = (msg) => {
      setIsLoading(false);
      setErrormsg({
        msg: msg || 'Codigo invalido.',
        show: true,
        severity: 'error',
      });
    };

    auth.verify2FA(code, trustDevice, null, redirectTo, cbErr);
  };

  const handleResend = () => {
    if (resendCooldown > 0) return;

    setIsResending(true);
    setErrormsg((prev) => ({ ...prev, show: false }));
    setSuccessMsg((prev) => ({ ...prev, show: false }));

    const cbSuccess = () => {
      setIsResending(false);
      setResendCooldown(60); // 60 segundos de cooldown
      setSuccessMsg({
        msg: 'Codigo reenviado com sucesso!',
        show: true,
      });
    };

    const cbErr = (msg) => {
      setIsResending(false);
      setErrormsg({
        msg: msg || 'Erro ao reenviar codigo.',
        show: true,
        severity: 'error',
      });
    };

    auth.resend2FACode(cbSuccess, cbErr);
  };

  const handleCancel = () => {
    auth.cancel2FA();
  };

  const handleCodeChange = (e) => {
    // Permite apenas numeros e limita a 6 caracteres
    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
    setCode(value);
  };

  if (!auth.pending2FA) {
    return null;
  }

  return (
    <ThemeProvider theme={theme}>
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <Paper
          elevation={2}
          sx={{
            p: 5,
            backgroundColor:
              theme.palette.mode === 'light' ? grey[100] : grey[900],
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <img
            style={{ width: '100%', marginBottom: 16 }}
            src={theme.palette.mode === 'light' ? logoBlack : logoWhite}
            alt="Logo"
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <EmailOutlinedIcon sx={{ fontSize: 48, color: blue[500], mb: 1 }} />
            <Typography component="h1" variant="h6" align="center">
              Verificacao em Duas Etapas
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ mt: 1 }}
            >
              Um codigo foi enviado para{' '}
              <strong>{auth.pending2FA.masked_email}</strong>
            </Typography>
          </Box>

          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{ mt: 1, width: '100%' }}
          >
            <TextField
              margin="normal"
              required
              fullWidth
              id="code"
              label="Codigo de 6 digitos"
              name="code"
              autoComplete="one-time-code"
              autoFocus
              value={code}
              onChange={handleCodeChange}
              disabled={isLoading}
              inputProps={{
                maxLength: 6,
                style: {
                  textAlign: 'center',
                  letterSpacing: '0.5em',
                  fontSize: '1.5rem',
                },
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={trustDevice}
                  onChange={(e) => setTrustDevice(e.target.checked)}
                  color="primary"
                  disabled={isLoading}
                />
              }
              label="Confiar neste dispositivo por 30 dias"
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading || code.length !== 6}
              sx={{ mt: 2, mb: 1 }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Verificar'
              )}
            </Button>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 2,
              }}
            >
              <MuiLink
                component="button"
                type="button"
                variant="body2"
                onClick={handleCancel}
                sx={{ cursor: 'pointer' }}
              >
                Voltar ao login
              </MuiLink>

              <MuiLink
                component="button"
                type="button"
                variant="body2"
                onClick={handleResend}
                disabled={resendCooldown > 0 || isResending}
                sx={{
                  cursor: resendCooldown > 0 ? 'default' : 'pointer',
                  color: resendCooldown > 0 ? 'text.disabled' : 'primary.main',
                }}
              >
                {isResending ? (
                  <CircularProgress size={14} sx={{ mr: 1 }} />
                ) : null}
                {resendCooldown > 0
                  ? `Reenviar em ${resendCooldown}s`
                  : 'Reenviar codigo'}
              </MuiLink>
            </Box>

            {successMsg.show && (
              <Alert severity="success" sx={{ mt: 2 }}>
                {successMsg.msg}
              </Alert>
            )}

            {errorMsg.show && (
              <Alert severity={errorMsg.severity} sx={{ mt: 2 }}>
                {errorMsg.msg}
              </Alert>
            )}
          </Box>
        </Paper>
      </Container>
    </ThemeProvider>
  );
}
